/**
 * Evidence Library Page (DC-1058)
 * ============================================================================
 * Unified Evidence Library displaying both regulatory citations (FDA, EMA, ICH)
 * and literature citations (PubMed) in a searchable, filterable interface.
 *
 * Features:
 * - Two tabs: Regulatory | Literature
 * - Framework/topic sidebar navigation
 * - Search across both citation types
 * - Export functionality
 * - Expandable requirement/citation cards
 *
 * @version 1.0.0
 * @created 2026-01-25
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FrameworkSidebar } from '../../components/evidence/FrameworkSidebar';
import { RequirementCard } from '../../components/evidence/RequirementCard';
import { CitationDrawer } from '../../components/evidence/CitationDrawer';
import { CitationExporter } from '../../components/evidence/CitationExporter';
import type {
  RegulatoryRequirement,
  RegulatoryCitation,
  RegulatoryFrameworkInfo,
  RegulatoryLibraryMetadata,
  LiteratureTopic,
  CitationType,
} from '../../types/evidence';
import type { LiteratureCitationRef } from '../../types/bridgeIntelligence';
import './evidence-library.css';

// ============================================================================
// Types
// ============================================================================

type ActiveTab = 'regulatory' | 'literature';

// Extended citation type to handle API response variations
interface ExtendedLiteratureCitation extends LiteratureCitationRef {
  publication_year?: number;
}

interface EvidenceLibraryState {
  activeTab: ActiveTab;
  searchQuery: string;
  selectedFramework: string | null;
  // Regulatory state
  requirements: RegulatoryRequirement[];
  frameworks: RegulatoryFrameworkInfo[];
  metadata: RegulatoryLibraryMetadata | null;
  expandedId: string | null;
  expandedDetail: RegulatoryRequirement | null;
  // Literature state
  literatureTopics: LiteratureTopic[];
  literatureCitations: LiteratureCitationRef[];
  selectedTopic: string | null;
  viewingTopicCitations: boolean;
  loadingCitations: boolean;
  // UI state
  loading: boolean;
  loadingDetail: boolean;
  error: string | null;
  // Drawer state
  drawerOpen: boolean;
  drawerCitation: RegulatoryCitation | LiteratureCitationRef | null;
  drawerCitationType: CitationType | null;
}

// ============================================================================
// Search Icon Component
// ============================================================================

const SearchIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

// ============================================================================
// Main Component
// ============================================================================

export const EvidenceLibraryPage: React.FC = () => {
  // ============================================================================
  // State
  // ============================================================================

  const [state, setState] = useState<EvidenceLibraryState>({
    activeTab: 'regulatory',
    searchQuery: '',
    selectedFramework: null,
    requirements: [],
    frameworks: [],
    metadata: null,
    expandedId: null,
    expandedDetail: null,
    literatureTopics: [],
    literatureCitations: [],
    selectedTopic: null,
    viewingTopicCitations: false,
    loadingCitations: false,
    loading: true,
    loadingDetail: false,
    error: null,
    drawerOpen: false,
    drawerCitation: null,
    drawerCitationType: null,
  });

  // ============================================================================
  // Data Fetching
  // ============================================================================

  const fetchRequirements = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const response = await fetch('/api/v1/regulatory/library/requirements');
      if (!response.ok) {
        throw new Error(`Failed to fetch requirements: ${response.status}`);
      }

      const data = await response.json();
      setState(prev => ({
        ...prev,
        requirements: data.requirements || [],
        metadata: data.metadata || null,
        loading: false,
      }));
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Failed to load requirements',
        loading: false,
      }));
    }
  }, []);

  const fetchFrameworks = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/regulatory/library/frameworks');
      if (response.ok) {
        const data = await response.json();
        setState(prev => ({
          ...prev,
          frameworks: data.frameworks || [],
        }));
      }
    } catch (err) {
      console.error('Failed to fetch frameworks:', err);
    }
  }, []);

  const fetchRequirementDetail = useCallback(async (id: string) => {
    try {
      setState(prev => ({ ...prev, loadingDetail: true }));
      const response = await fetch(`/api/v1/regulatory/library/requirements/${encodeURIComponent(id)}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch requirement: ${response.status}`);
      }
      const data = await response.json();
      setState(prev => ({
        ...prev,
        expandedDetail: data.requirement || null,
        loadingDetail: false,
      }));
    } catch (err) {
      console.error('Failed to fetch requirement detail:', err);
      setState(prev => ({
        ...prev,
        expandedDetail: null,
        loadingDetail: false,
      }));
    }
  }, []);

  const fetchLiteratureTopics = useCallback(async () => {
    // Fetch actual stats from the database
    try {
      const response = await fetch('/api/v1/literature/curated/stats');
      if (response.ok) {
        const data = await response.json();

        // Map category names to friendly display names
        const categoryNames: Record<string, { name: string; description: string }> = {
          fair_principle: { name: 'FAIR Data Principles', description: 'Foundational FAIR literature (F, A, I, R principles)' },
          methodology: { name: 'Research Methodology', description: 'ML readiness, data quality, privacy, reproducibility' },
          domain_specific: { name: 'Domain Standards', description: 'Genomics and clinical data standards' },
          ml_readiness: { name: 'ML Readiness', description: 'Machine learning data quality and readiness' },
          data_quality: { name: 'Data Quality', description: 'Data completeness, validity, and consistency' },
          privacy: { name: 'Privacy & Security', description: 'k-anonymity, differential privacy, de-identification' },
          governance: { name: 'Data Governance', description: 'Data stewardship and governance frameworks' },
        };

        const topics: LiteratureTopic[] = [
          { id: 'all', name: 'All Citations', citationCount: data.total || 0, description: 'Browse all curated scientific citations' },
        ];

        // Add topics from database stats
        for (const cat of data.byCategory || []) {
          const meta = categoryNames[cat.category] || { name: cat.category, description: '' };
          topics.push({
            id: cat.category,
            name: meta.name,
            citationCount: cat.count,
            description: meta.description,
          });
        }

        setState(prev => ({
          ...prev,
          literatureTopics: topics,
        }));
        return;
      }
    } catch (err) {
      console.error('Failed to fetch literature stats:', err);
    }

    // Fallback to defaults if API fails
    const defaultTopics: LiteratureTopic[] = [
      { id: 'all', name: 'All Citations', citationCount: 0, description: 'Browse all curated scientific citations' },
      { id: 'fair_principle', name: 'FAIR Data Principles', citationCount: 0, description: 'Foundational FAIR literature (F, A, I, R principles)' },
      { id: 'methodology', name: 'Research Methodology', citationCount: 0, description: 'ML readiness, data quality, privacy, reproducibility' },
      { id: 'domain_specific', name: 'Domain Standards', citationCount: 0, description: 'Genomics and clinical data standards' },
    ];
    setState(prev => ({
      ...prev,
      literatureTopics: defaultTopics,
    }));
  }, []);

  const fetchCitationsForTopic = useCallback(async (topicId: string) => {
    setState(prev => ({ ...prev, loadingCitations: true }));
    try {
      // Topic ID maps to category in the curated citations table
      // 'all' is a special case - fetch without category filter to get common citations
      const url = topicId === 'all'
        ? '/api/v1/literature/curated?limit=100'
        : `/api/v1/literature/curated?category=${encodeURIComponent(topicId)}&limit=100`;

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setState(prev => ({
          ...prev,
          literatureCitations: data.citations || [],
          loadingCitations: false,
        }));
      } else {
        // If API fails, show empty list
        console.error('Failed to fetch citations:', response.status);
        setState(prev => ({
          ...prev,
          literatureCitations: [],
          loadingCitations: false,
        }));
      }
    } catch (err) {
      console.error('Failed to fetch citations:', err);
      setState(prev => ({
        ...prev,
        literatureCitations: [],
        loadingCitations: false,
      }));
    }
  }, []);

  // Initial data load
  useEffect(() => {
    fetchRequirements();
    fetchFrameworks();
    fetchLiteratureTopics();
  }, [fetchRequirements, fetchFrameworks, fetchLiteratureTopics]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleTabChange = (tab: ActiveTab) => {
    setState(prev => ({
      ...prev,
      activeTab: tab,
      selectedFramework: null,
      selectedTopic: null,
      viewingTopicCitations: false,
      literatureCitations: [],
      expandedId: null,
      expandedDetail: null,
    }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({ ...prev, searchQuery: e.target.value }));
  };

  const handleClearSearch = () => {
    setState(prev => ({ ...prev, searchQuery: '' }));
  };

  const handleFrameworkSelect = (framework: string | null) => {
    setState(prev => ({ ...prev, selectedFramework: framework }));
  };

  const handleTopicSelect = (topicId: string | null) => {
    if (topicId) {
      setState(prev => ({
        ...prev,
        selectedTopic: topicId,
        viewingTopicCitations: true,
      }));
      fetchCitationsForTopic(topicId);
    } else {
      setState(prev => ({
        ...prev,
        selectedTopic: null,
        viewingTopicCitations: false,
        literatureCitations: [],
      }));
    }
  };

  const handleBackToTopics = () => {
    setState(prev => ({
      ...prev,
      selectedTopic: null,
      viewingTopicCitations: false,
      literatureCitations: [],
    }));
  };

  const handleToggleExpand = (id: string) => {
    if (state.expandedId === id) {
      setState(prev => ({
        ...prev,
        expandedId: null,
        expandedDetail: null,
      }));
    } else {
      setState(prev => ({ ...prev, expandedId: id }));
      fetchRequirementDetail(id);
    }
  };

  const handleViewCitation = (citation: RegulatoryCitation | LiteratureCitationRef, type: CitationType) => {
    setState(prev => ({
      ...prev,
      drawerOpen: true,
      drawerCitation: citation,
      drawerCitationType: type,
    }));
  };

  const handleCloseDrawer = () => {
    setState(prev => ({
      ...prev,
      drawerOpen: false,
      drawerCitation: null,
      drawerCitationType: null,
    }));
  };

  // ============================================================================
  // Filtered Data
  // ============================================================================

  const filteredRequirements = useMemo(() => {
    let filtered = state.requirements;

    // Filter by framework
    if (state.selectedFramework) {
      filtered = filtered.filter(req =>
        req.frameworks.includes(state.selectedFramework as RegulatoryRequirement['frameworks'][number])
      );
    }

    // Filter by search query
    if (state.searchQuery.trim()) {
      const query = state.searchQuery.toLowerCase();
      filtered = filtered.filter(req =>
        req.principle.toLowerCase().includes(query) ||
        req.requirement.toLowerCase().includes(query) ||
        req.rationale.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [state.requirements, state.selectedFramework, state.searchQuery]);

  const filteredTopics = useMemo(() => {
    if (!state.searchQuery.trim()) {
      return state.literatureTopics;
    }
    const query = state.searchQuery.toLowerCase();
    return state.literatureTopics.filter(topic =>
      topic.name.toLowerCase().includes(query) ||
      (topic.description?.toLowerCase().includes(query))
    );
  }, [state.literatureTopics, state.searchQuery]);

  // ============================================================================
  // Counts
  // ============================================================================

  const regulatoryCount = state.requirements.length;
  const literatureCount = state.literatureTopics.reduce((sum, t) => sum + t.citationCount, 0);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="evidence-library">
      {/* Header */}
      <header className="evidence-library__header">
        <div className="evidence-library__header-left">
          <h2 className="evidence-library__title">Evidence Library</h2>
          {state.metadata && (
            <span className="evidence-library__meta">
              v{state.metadata.version} | {state.metadata.alcoaRequirementCount} ALCOA+ |{' '}
              {state.metadata.fdaRequirementCount} FDA Credibility | {state.metadata.totalCitations} Citations
            </span>
          )}
        </div>
        <div className="evidence-library__header-right">
          {/* Search */}
          <div className="evidence-library__search">
            <span className="evidence-library__search-icon">
              <SearchIcon />
            </span>
            <input
              type="text"
              className="evidence-library__search-input"
              placeholder="Search evidence..."
              value={state.searchQuery}
              onChange={handleSearchChange}
              aria-label="Search evidence library"
            />
            {state.searchQuery && (
              <button
                className="evidence-library__search-clear"
                onClick={handleClearSearch}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
          {/* Export */}
          <CitationExporter
            citations={state.activeTab === 'regulatory'
              ? filteredRequirements.flatMap(r => r.citations || [])
              : state.literatureCitations
            }
            citationType={state.activeTab}
          />
        </div>
      </header>

      {/* Tabs */}
      <div className="evidence-library__tabs" role="tablist">
        <button
          role="tab"
          className={`evidence-library__tab ${state.activeTab === 'regulatory' ? 'evidence-library__tab--active' : ''}`}
          aria-selected={state.activeTab === 'regulatory'}
          onClick={() => handleTabChange('regulatory')}
        >
          Regulatory
          <span className="evidence-library__tab-count">{regulatoryCount}</span>
        </button>
        <button
          role="tab"
          className={`evidence-library__tab ${state.activeTab === 'literature' ? 'evidence-library__tab--active' : ''}`}
          aria-selected={state.activeTab === 'literature'}
          onClick={() => handleTabChange('literature')}
        >
          Literature
          <span className="evidence-library__tab-count">{literatureCount}</span>
        </button>
      </div>

      {/* Body */}
      <div className="evidence-library__body">
        {/* Sidebar */}
        <FrameworkSidebar
          activeTab={state.activeTab}
          selectedFramework={state.selectedFramework}
          onSelectFramework={handleFrameworkSelect}
          frameworks={state.frameworks}
          literatureTopics={filteredTopics}
          selectedTopic={state.selectedTopic}
          onSelectTopic={handleTopicSelect}
        />

        {/* Main Content */}
        <main className="evidence-library__content">
          {state.loading ? (
            <div className="evidence-library__loading">
              <div className="evidence-library__loading-spinner" />
              <p>Loading evidence library...</p>
            </div>
          ) : state.error ? (
            <div className="evidence-library__error">
              <span className="evidence-library__error-icon">!</span>
              <p className="evidence-library__error-message">{state.error}</p>
              <button className="evidence-library__retry-btn" onClick={fetchRequirements}>
                Retry
              </button>
            </div>
          ) : state.activeTab === 'regulatory' ? (
            // Regulatory Content
            filteredRequirements.length === 0 ? (
              <div className="evidence-library__empty">
                <span className="evidence-library__empty-icon">📋</span>
                <p>No requirements found matching your criteria.</p>
              </div>
            ) : (
              <div className="evidence-library__requirements">
                {state.searchQuery && (
                  <div className="evidence-library__search-results-header">
                    <h3>Search Results</h3>
                    <span className="evidence-library__search-results-count">
                      {filteredRequirements.length} requirement{filteredRequirements.length !== 1 ? 's' : ''} found
                    </span>
                  </div>
                )}
                {filteredRequirements.map(req => (
                  <RequirementCard
                    key={req.id}
                    requirement={req}
                    expanded={state.expandedId === req.id}
                    expandedDetail={state.expandedId === req.id ? state.expandedDetail : null}
                    loadingDetail={state.expandedId === req.id && state.loadingDetail}
                    onToggleExpand={() => handleToggleExpand(req.id)}
                    onViewCitation={(citation) => handleViewCitation(citation, 'regulatory')}
                    searchQuery={state.searchQuery}
                  />
                ))}
              </div>
            )
          ) : (
            // Literature Content
            <div className="evidence-library__literature">
              {state.viewingTopicCitations ? (
                // Viewing citations for a specific topic
                <>
                  <div className="evidence-library__citations-header">
                    <button
                      className="evidence-library__back-btn"
                      onClick={handleBackToTopics}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                      </svg>
                      Back to Topics
                    </button>
                    <h3>
                      {state.literatureTopics.find(t => t.id === state.selectedTopic)?.name || 'Citations'}
                    </h3>
                    <span className="evidence-library__citations-count">
                      {state.literatureCitations.length} citation{state.literatureCitations.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {state.loadingCitations ? (
                    <div className="evidence-library__loading">
                      <div className="evidence-library__loading-spinner" />
                      <p>Loading citations...</p>
                    </div>
                  ) : state.literatureCitations.length === 0 ? (
                    <div className="evidence-library__empty">
                      <span className="evidence-library__empty-icon">📚</span>
                      <p>No citations found for this topic.</p>
                    </div>
                  ) : (
                    <div className="evidence-library__citations-list">
                      {state.literatureCitations.map((citation, index) => (
                        <div
                          key={citation.pmid || index}
                          className="evidence-library__citation-card"
                          onClick={() => handleViewCitation(citation, 'literature')}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && handleViewCitation(citation, 'literature')}
                        >
                          <div className="evidence-library__citation-header">
                            <h4 className="evidence-library__citation-title">{citation.title}</h4>
                            {citation.pmid && (
                              <span className="evidence-library__citation-pmid">PMID: {citation.pmid}</span>
                            )}
                          </div>
                          <p className="evidence-library__citation-authors">
                            {typeof citation.authors === 'string'
                              ? citation.authors
                              : (Array.isArray(citation.authors) ? (citation.authors as string[]).join(', ') : 'Unknown authors')
                            }
                          </p>
                          <p className="evidence-library__citation-journal">
                            {citation.journal}{(citation.year || (citation as ExtendedLiteratureCitation).publication_year) ? ` (${citation.year || (citation as ExtendedLiteratureCitation).publication_year})` : ''}
                          </p>
                          {citation.key_quote && (
                            <blockquote className="evidence-library__citation-quote">
                              "{citation.key_quote}"
                            </blockquote>
                          )}
                          {citation.relevance_score !== undefined && (
                            <div className="evidence-library__citation-relevance">
                              <span className="evidence-library__citation-relevance-label">Relevance</span>
                              <div className="evidence-library__citation-relevance-bar">
                                <div
                                  className="evidence-library__citation-relevance-fill"
                                  style={{ width: `${citation.relevance_score}%` }}
                                />
                              </div>
                              <span className="evidence-library__citation-relevance-value">
                                {Math.round(citation.relevance_score)}%
                              </span>
                            </div>
                          )}
                          <span className="evidence-library__citation-view">Click to view details →</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                // Topic selection view
                <>
                  <div className="evidence-library__literature-intro">
                    <h3>Scientific Literature</h3>
                    <p>
                      Browse curated scientific citations that support FAIR data practices, ML/AI readiness
                      assessments, and data quality standards. Citations are sourced from PubMed.
                    </p>
                  </div>
                  {filteredTopics.length === 0 ? (
                    <div className="evidence-library__empty">
                      <span className="evidence-library__empty-icon">📚</span>
                      <p>No literature topics found matching your search.</p>
                    </div>
                  ) : (
                    <div className="evidence-library__literature-topics">
                      {filteredTopics.map(topic => (
                        <div
                          key={topic.id}
                          className={`evidence-library__topic-card ${state.selectedTopic === topic.id ? 'evidence-library__topic-card--selected' : ''}`}
                          onClick={() => handleTopicSelect(topic.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && handleTopicSelect(topic.id)}
                        >
                          <h4>{topic.name}</h4>
                          {topic.description && <p>{topic.description}</p>}
                          <span className="evidence-library__topic-count">
                            {topic.citationCount} citation{topic.citationCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Citation Drawer */}
      <CitationDrawer
        isOpen={state.drawerOpen}
        onClose={handleCloseDrawer}
        citation={state.drawerCitation}
        citationType={state.drawerCitationType}
      />
    </div>
  );
};

export default EvidenceLibraryPage;
